// EventsTable.js
import React, { useState, useMemo } from "react";
import {
    Card,
    CardBody,
    Table,
    Input,
    Row,
    Col,
    Button,
} from "reactstrap";
import PropTypes from "prop-types";
import DateRangeSelector from "components/Common/DateRangeSelector";
import DeleteItemModal from "pages/MonthlyParking/DeleteItemModal";
import EditEventModal from "./EditEventModal";
import { deleteEvent } from "helpers/fakebackend_helper";
import formatDate from "constants/formatDate";
import formatCurrency from "constants/formatCurrency";
import formatPercentage from "constants/formatPercentage";

const EventsTable = ({ events, locations, loadEvents }) => {
    const user = JSON.parse(localStorage.getItem("authUser"));
    const [search, setSearch] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "event_date", direction: "asc" });
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const toggleDeleteModal = () => setDeleteModalOpen(!deleteModalOpen);
    const toggleEditModal = () => setEditModalOpen(!editModalOpen);

    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    const onDelete = async (values) => {
        await deleteEvent(user.token, values.id)
        loadEvents()
    }

    const sortedFilteredEvents = useMemo(() => {
        return events
            .filter((e) => {
                const matchesSearch =
                    (e.event_name || "").toLowerCase().includes(search.toLowerCase()) ||
                    (e.event_note || "").toLowerCase().includes(search.toLowerCase());

                const matchesStart = startDate ? new Date(e.event_date) >= new Date(startDate) : true;
                const matchesEnd = endDate ? new Date(e.event_date) <= new Date(endDate) : true;

                return matchesSearch && matchesStart && matchesEnd;
            })
            .sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (["invoice_amount", "labor_cost", "non_labor_cost"].includes(sortConfig.key)) {
                    aVal = parseFloat(aVal) || 0;
                    bVal = parseFloat(bVal) || 0;
                }

                if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
                return 0;
            });
    }, [events, search, startDate, endDate, sortConfig]);

    return (
        <Col xl={12}>
            <Card className="accordion-gradient-border" style={{ background: "#3C3C3C" }}>
                <CardBody>

                    <Row className="mb-3 d-flex align-items-center gap-2">
                        <Col sm="12">
                            <DateRangeSelector onApply={(start, end) => {
                                setStartDate(start);
                                setEndDate(end);
                            }} />
                        </Col>

                        <Col sm="12">
                            <Input
                                type="text"
                                placeholder="Search by name or note"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Col>
                    </Row>

                    <Table className="align-middle table-hover table-dark table-nowrap mb-0" responsive>
                        <thead className="table-success">
                            <tr>
                                {["event_name", "event_date", "invoice_amount", "labor_cost", "non_labor_cost", "total_cost", "net_profit", "profit_pct"].map((key, idx) => (
                                    <th
                                        key={idx}
                                        onClick={() => handleSort(key)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {key === "event_name" && "Event"}
                                        {key === "event_date" && "Date"}
                                        {key === "invoice_amount" && "Total Invoice"}
                                        {key === "labor_cost" && "Total Labor Cost"}
                                        {key === "non_labor_cost" && "Non-Labor Cost"}
                                        {key === "total_cost" && "Total Cost"}
                                        {key === "net_profit" && "Net Profit"}
                                        {key === "profit_pct" && "%"}
                                        {sortConfig.key === key ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : ""}
                                    </th>
                                ))}
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedFilteredEvents.map((event, idx) => {
                                const totalCost = (parseFloat(event.labor_cost) || 0) + (parseFloat(event.non_labor_cost) || 0);
                                const netProfit = (parseFloat(event.invoice_amount) || 0) - totalCost;

                                return (
                                    <tr key={idx}>
                                        <td>{event.event_name}</td>
                                        <td>{formatDate(event.event_date)}</td>
                                        <td>${formatCurrency(event.invoice_amount)}</td>
                                        <td>${formatCurrency(event.labor_cost)}</td>
                                        <td>${formatCurrency(event.non_labor_cost)}</td>
                                        <td>${formatCurrency(totalCost)}</td>
                                        <td>${formatCurrency(netProfit)}</td>
                                        <td>{formatPercentage(netProfit, event.invoice_amount)}%</td>
                                        <td>
                                            <Button
                                                size="sm"
                                                color="link"
                                                onClick={() => {
                                                    setSelectedEvent(event);
                                                    toggleEditModal();
                                                }}
                                            >
                                                <i className="fa fa-edit"></i>
                                            </Button>
                                            <Button
                                                size="sm"
                                                color="link"
                                                onClick={() => {
                                                    setSelectedEvent(event);
                                                    toggleDeleteModal();
                                                }}
                                            >
                                                <i className="fa fa-trash text-danger"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>

            <DeleteItemModal
                isOpen={deleteModalOpen}
                toggle={toggleDeleteModal}
                onConfirm={() => {
                    onDelete(selectedEvent);
                    toggleDeleteModal();
                }}
                itemLabel="event"
            />

            {selectedEvent && (
                <EditEventModal
                    isOpen={editModalOpen}
                    toggle={toggleEditModal}
                    locations={locations}
                    eventData={selectedEvent}
                    handleSave={loadEvents}
                />
            )}
        </Col>
    );
};

EventsTable.propTypes = {
    title: PropTypes.string,
    events: PropTypes.array.isRequired,
    onDelete: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
};

export default EventsTable;